/**
 * 티켓 댓글 — 진행 문의와 확인 왕복이 티켓 안에서 오간다.
 *
 * 역할 가상 검증(2026-09-01) 셋이 「텔레그램을 못 끊는다」로 끝났고 그
 * 공통 이유 첫째가 이 자리였다 — 「"고쳤어요" "아직 안 돼요" 왕복이 티켓
 * 안에서 불가능하다」. 본문 수정은 통째로 갈아끼우는 방식이라 둘이 동시에
 * 고치면 한쪽이 사라졌는데, **댓글은 한 마디가 행 하나라 동시에 써도
 * 서로를 안 덮는다.**
 *
 * - **보는 사람은 말할 수 있다** — 서버가 읽기·쓰기 모두 `ticket.view` 로
 *   판정한다. 이 모달이 열렸다는 것이 곧 그 권한이 있다는 뜻이라, 입력칸을
 *   권한으로 또 가리지 않는다.
 * - **고치기는 자기 것만, 지우기는 자기 것 + 관리자.** 단추는 화면의
 *   조심이고 판정은 서버다(`routers/comments.py`).
 * - **오류는 이 절 안에 적는다.** 전역 배너(`onError`)는 모달 뒤에 가려
 *   안 보인다 — AI 요약이 같은 이유로 같은 선택을 했다(실측).
 *
 * ## `@이름` — 알림이 안 가던 구멍을 메운다
 *
 * 알림 수신자를 서버가 「생성자 + 작업자」로 파생하는데, 확정 사항이
 * **「기획·QA 가 만든 티켓은 작업자가 빈다」**이다. 그래서 QA 가 기획
 * 티켓에 「이거 재현됩니다」라고 써도 **개발에게 안 갔고**, 그 한 마디가
 * 다시 텔레그램으로 갔다.
 *
 * **화면이 보내는 것은 그냥 본문 글자다** — 멘션용 칸이 따로 없다. 서버가
 * 본문을 사람 목록과 맞춰 본다(`backend/app/mentions.py`). 그래서 여기서
 * 하는 일은 **사람이 `@이름` 을 정확히 적게 돕는 것 하나**이고, 이 고르기가
 * 안 떠도 손으로 친 `@이름` 은 똑같이 걸린다.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { imageFilesFromDataTransfer, uploadAndInsert } from '../attachments';

import { Button } from '@/components/ui/button';

import {
  createComment,
  deleteComment,
  errorMessage,
  listComments,
  listMembers,
  NotLoggedInError,
  updateComment,
  type TicketComment,
  type User,
  type UserBrief,
  ROLE_LABEL,
} from '../api';
import { AssigneeMark, BADGE } from '../board/Chips';
import { formatWhen } from '../format';
import { SectionTitle } from './section';

/** 후보를 몇 명까지 세울까. 목록이 길면 화면을 덮어 쓰던 글이 안 보인다. */
const MENTION_MAX = 8;

/** 항목마다 고유한 id — `aria-activedescendant` 가 이것을 가리킨다. */
function optionId(userId: number): string {
  return `comment-mention-${userId}`;
}

/**
 * 커서 왼쪽에서 **지금 치고 있는 `@…`** 를 찾는다. 없으면 `null`.
 *
 * ⚠ **`@` 앞이 글자면 멘션이 아니다.** 이메일 주소(`gary@viewster.io`)가
 * 그 모양이라, 주소를 적는 동안 목록이 계속 튀어나온다.
 *
 * ⚠ **공백에서 끊는다.** 공백을 이름의 일부로 받으면 뒤에 치는 문장 전체가
 * 후보 글자가 돼 목록이 영영 안 닫힌다. 공백 든 이름은 앞부분만 치고
 * 고르면 된다 — **넣는 것은 이름 전체**다.
 */
function mentionToken(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at < 0) return null;
  const before = at > 0 ? upto[at - 1] : '';
  if (before !== '' && !/\s/.test(before)) return null;
  const query = upto.slice(at + 1);
  if (/\s/.test(query)) return null;
  return { start: at, query };
}

export function Comments({
  ticketId,
  user,
  rail,
  onUnauthorized,
}: {
  ticketId: number;
  user: User;
  /** 인용선 색. 상세의 다른 두 절(티켓 내용·AI 요약)과 같은 값을 받는다 —
   *  셋이 같은 레일 위에 서야 한 층위로 읽힌다. */
  rail: string;
  onUnauthorized: () => void;
}) {
  /** `null` 은 「아직 못 읽었다」다 — 빈 목록(`[]`)과 다르다. */
  const [comments, setComments] = useState<TicketComment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // 붙여넣기로 올리는 중인가. 올리는 동안 등록을 막는다 — 조각이 안 들어간
  // 채로 등록되면 사람은 사진이 붙은 줄 안다.
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  /** 고치는 중인 댓글 하나. 동시에 여럿을 고치는 화면은 안 만든다 —
   *  자기 것만 고칠 수 있으니 「여럿」은 자기 창 하나 안의 혼란일 뿐이다. */
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const areaRef = useRef<HTMLTextAreaElement>(null);
  /** 부를 수 있는 사람. `null` 은 「아직 안 불렀다」다. */
  const [members, setMembers] = useState<UserBrief[] | null>(null);
  /** 한 번만 부른다 — 상세를 여는 동안 사람 목록이 바뀔 일이 없다. */
  const membersAsked = useRef(false);
  /** 지금 치고 있는 `@…`. `null` 이면 목록을 안 그린다. */
  const [mention, setMention] = useState<{ start: number; query: string } | null>(
    null,
  );
  /** 목록에서 고른 자리. 글자가 바뀔 때마다 맨 위로 돌아간다. */
  const [active, setActive] = useState(0);
  /**
   * 골라 넣은 뒤 커서를 둘 자리. `null` 은 「옮길 것 없음」이다.
   *
   * **상태가 아니라 ref 다** — 화면에 그려지는 값이 아니고, 상태로 두면
   * 옮긴 뒤 되돌리는 `setState` 가 그릴 때마다 한 번 더 그리게 만든다.
   */
  const caretTo = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await listComments(ticketId);
        if (cancelled) return;
        setComments(data.comments);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotLoggedInError) {
          onUnauthorized();
          return;
        }
        // 댓글이 안 읽혀도 티켓은 봐야 한다 — 이 절 안에서만 말한다.
        setError(`댓글을 불러오지 못했다: ${errorMessage(err)}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketId, onUnauthorized]);

  /**
   * 사람 목록은 **`@` 를 처음 칠 때** 부른다. 상세를 여는 것만으로는 안
   * 부른다 — 같은 목록을 상세 화면이 이미 자기 몫으로 부르고 있어, 댓글을
   * 안 쓰는 사람에게는 두 번 부르는 것이 된다.
   */
  function askMembers() {
    if (membersAsked.current) return;
    membersAsked.current = true;
    void (async () => {
      try {
        const data = await listMembers();
        setMembers(data.members);
      } catch {
        // **오류를 이 자리에 안 세운다.** 고르기가 안 떠도 손으로 친
        // `@이름` 은 서버가 똑같이 읽는다 — 잃는 것은 편의뿐인데, 댓글을
        // 쓰려던 사람 앞에 빨간 줄을 세우면 그것이 더 큰 방해다.
      }
    })();
  }

  /** 지금 목록에 세울 사람. 맞는 사람이 없으면 아무것도 안 띄운다. */
  const matches = useMemo(() => {
    if (!mention || !members) return [];
    const q = mention.query.toLowerCase();
    // **앞부분만 맞추지 않는다** — 「김개발」을 「개발」로 찾는 것이 실제로
    // 사람이 하는 일이다.
    return members
      .filter((m) => m.display_name.toLowerCase().includes(q))
      .slice(0, MENTION_MAX);
  }, [mention, members]);
  const picking = matches.length > 0;
  const activeIndex = matches.length === 0 ? 0 : Math.min(active, matches.length - 1);

  /** 글자가 바뀌거나 커서가 움직일 때마다 `@…` 를 다시 잰다. */
  function trackMention(area: HTMLTextAreaElement) {
    const token = mentionToken(area.value, area.selectionStart);
    setMention(token);
    if (token) askMembers();
  }

  function pick(member: UserBrief) {
    if (!mention) return;
    const before = draft.slice(0, mention.start);
    const after = draft.slice(mention.start + 1 + mention.query.length);
    // ⚠ **뒤에 공백 하나를 붙인다.** 안 붙이면 다음에 치는 글자가 이름에
    // 그대로 붙고, 서버의 경계 판정(`mentions._boundary_ok`)이 「더 긴
    // 이름의 앞부분을 잘라 먹었다」로 보아 **안 맞은 것으로 넘긴다.**
    const inserted = `@${member.display_name} `;
    setDraft(before + inserted + after);
    setMention(null);
    caretTo.current = before.length + inserted.length;
  }

  // 커서를 넣은 글자 뒤에 둔다. 안 옮기면 브라우저가 끝으로 보내서,
  // 문장 가운데서 부른 사람 뒤를 이어 쓸 수 없다.
  //
  // **의존 배열이 없다** — 새 글자가 실제로 그려진 뒤라야 그 자리로 옮길
  // 수 있고, 옮길 것이 있는지는 ref 하나로 판정한다.
  useEffect(() => {
    const at = caretTo.current;
    if (at === null) return;
    caretTo.current = null;
    const area = areaRef.current;
    if (area) {
      area.focus();
      area.setSelectionRange(at, at);
    }
  });

  async function submit() {
    const body = draft.trim();
    // 올리는 중이면 막는다 — 조각이 안 들어간 채 등록되면 사람은 사진이
    // 붙은 줄 안다.
    if (!body || sending || uploading) return;
    setSending(true);
    setError(null);
    try {
      const made = await createComment(ticketId, body);
      // 서버가 단 순서(id)로 주므로 새것은 끝에 선다.
      setComments((prev) => [...(prev ?? []), made]);
      setDraft('');
      // 글이 통째로 비었으니 재던 `@…` 도 함께 버린다 — 안 버리면 낡은
      // 자리를 가리킨 채 목록이 남는다.
      setMention(null);
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      // **초안을 안 지운다** — 실패한 말이 사라지면 다시 쳐야 한다.
      setError(`댓글을 달지 못했다: ${errorMessage(err)}`);
    } finally {
      setSending(false);
    }
  }

  function startEdit(comment: TicketComment) {
    setEditingId(comment.id);
    setEditDraft(comment.body);
    setError(null);
  }

  async function saveEdit(commentId: number) {
    const body = editDraft.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await updateComment(ticketId, commentId, body);
      setComments((prev) =>
        (prev ?? []).map((c) => (c.id === commentId ? saved : c)),
      );
      setEditingId(null);
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      setError(`댓글을 고치지 못했다: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove(commentId: number) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteComment(ticketId, commentId);
      setComments((prev) => (prev ?? []).filter((c) => c.id !== commentId));
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      setError(`댓글을 지우지 못했다: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="댓글"
      className="border-l-[6px] pl-s4"
      style={{ borderColor: rail }}
    >
      <SectionTitle>
        댓글
        {comments !== null && comments.length > 0 && (
          <span className="tnum"> {comments.length}</span>
        )}
      </SectionTitle>

      {error && (
        <p className="m-0 mb-s3 text-meta text-st-red-f" role="alert">
          {error}
        </p>
      )}

      {comments === null ? (
        <p className="m-0 text-meta text-ink-3">댓글을 읽는 중…</p>
      ) : comments.length === 0 ? (
        <p className="m-0 text-meta text-ink-3">아직 댓글이 없다</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-s4 p-0">
          {comments.map((comment) => {
            const mine = comment.author?.id === user.id;
            const canDelete = mine || user.is_admin;
            return (
              <li key={comment.id}>
                <div className="flex flex-wrap items-center gap-s2">
                  {comment.author ? (
                    <>
                      <AssigneeMark user={comment.author} size={18} />
                      <span className="text-meta text-ink">
                        {comment.author.display_name}
                      </span>
                      <span className="text-micro text-ink-3">
                        {ROLE_LABEL[comment.author.role]}
                      </span>
                    </>
                  ) : comment.agent_author ? (
                    /* 에이전트가 쓴 말은 그 사실이 화면에 남아야 한다 —
                       AI 요약이 모델 이름을 적는 것과 같은 규칙이다. */
                    <>
                      <span className={`${BADGE} bg-sunk font-bold text-ink-2`}>
                        AI
                      </span>
                      <span className="text-meta text-ink">
                        {comment.agent_author}
                      </span>
                    </>
                  ) : (
                    /* 계정이 사라진 사람의 말. 생성자 칸과 같은 표기다. */
                    <span className="text-meta text-ink-3">알 수 없음</span>
                  )}
                  <span className="tnum text-micro text-ink-3">
                    {formatWhen(comment.created_at)}
                  </span>
                  {comment.edited && (
                    <span className="text-micro text-ink-3">수정됨</span>
                  )}
                  {editingId !== comment.id && canDelete && (
                    <span className="ml-auto flex gap-s1">
                      {mine && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-ink-3"
                          disabled={busy}
                          onClick={() => startEdit(comment)}
                        >
                          고치기
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-ink-3"
                          disabled={busy}
                          onClick={() => void remove(comment.id)}
                        >
                          지우기
                        </Button>
                      )}
                    </span>
                  )}
                </div>
                {editingId === comment.id ? (
                  <div className="mt-s2">
                    <textarea
                      value={editDraft}
                      onChange={(event) => setEditDraft(event.target.value)}
                      aria-label="댓글 고치기"
                      rows={3}
                      className="w-full resize-y rounded-md border border-line bg-panel px-s3 py-s2 text-body leading-body break-keep text-ink max-[720px]:text-lead"
                    />
                    <div className="mt-s2 flex justify-end gap-s2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingId(null)}
                      >
                        취소
                      </Button>
                      <Button
                        size="sm"
                        disabled={busy || !editDraft.trim()}
                        onClick={() => void saveEdit(comment.id)}
                      >
                        {busy ? '저장 중…' : '저장'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* 줄바꿈을 지킨다 — 티켓 본문과 같은 규칙이다. 저장된
                     글자는 마크다운이고, 본문이 마크다운 렌더를 얻는 날
                     여기도 같이 얻어야 한다(파서가 둘이면 갈린다). */
                  <p className="m-0 mt-s1 whitespace-pre-wrap text-body leading-body break-keep text-ink max-[720px]:text-lead">
                    {comment.body}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="relative mt-s4">
        <textarea
          ref={areaRef}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setActive(0);
            trackMention(event.target);
          }}
          /* 커서만 움직여도 다시 잰다 — 화살표나 마우스로 `@…` 밖으로
             나갔는데 목록이 남아 있으면, Enter 가 커서와 다른 자리에
             이름을 끼워 넣는다. */
          onSelect={(event) => trackMention(event.currentTarget)}
          /* 목록이 뜬 채로 다른 곳을 누르면 닫는다. 항목 쪽은
             `onMouseDown` 에서 포커스를 붙잡아 두므로 안 닫힌다. */
          onBlur={() => setMention(null)}
          onKeyDown={(event) => {
            // ⚠ **한글 조합 중에는 아무 키도 안 삼킨다.** 조합 중의 Enter 는
            // 글자를 확정하는 키다 — 가로채면 한글 입력이 깨진다.
            if (event.nativeEvent.isComposing) return;
            if (picking) {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActive((activeIndex + 1) % matches.length);
                return;
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActive((activeIndex - 1 + matches.length) % matches.length);
                return;
              }
              // **목록이 떠 있으면 Enter 는 고르기다** — 등록으로 새면
              // 부르려던 사람 이름이 반만 적힌 채 댓글이 달린다.
              // ⌘/Ctrl+Enter 는 그대로 등록이다(아래로 내려간다).
              if (
                (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) ||
                event.key === 'Tab'
              ) {
                event.preventDefault();
                pick(matches[activeIndex]);
                return;
              }
              if (event.key === 'Escape') {
                // ⚠ **모달 안이다.** 안 멈추면 목록과 상세가 함께 닫혀
                // 쓰던 글이 사라진다.
                event.preventDefault();
                event.stopPropagation();
                setMention(null);
                return;
              }
            }
            // 대화 입력칸의 관행 — Enter 는 줄바꿈, Ctrl/⌘+Enter 가 등록이다.
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void submit();
            }
          }}
          onPaste={(event) => {
            // 이미지를 붙여넣으면 올리고 마크다운 조각을 끼운다.
            //
            // ⚠ **QA 가 이것 하나로 텔레그램에 남아 있었다** — 오늘 역할
            // 검증에서 버그 셋을 신고하고 세 티켓 전부에 「스크린샷은
            // 텔레그램에 올림 (여기 첨부가 안 돼서)」이라고 적었다.
            const files = imageFilesFromDataTransfer(event.clipboardData);
            if (files.length === 0) return;
            // 글자 붙여넣기까지 막지는 않는다 — 이미지가 있을 때만 가로챈다.
            event.preventDefault();
            // 글이 밖에서 바뀐다 — 재던 `@…` 는 여기서 버린다.
            setMention(null);
            setUploading(true);
            setUploadError(null);
            void (async () => {
              try {
                const parts: string[] = [];
                for (const file of files) parts.push(await uploadAndInsert(file));
                // **끝에 붙인다.** 커서 자리에 끼우려면 selectionStart 를
                // 읽어야 하는데, 올리는 동안 사람이 계속 타이핑하면 그 값이
                // 낡는다 — 끼워 넣은 조각이 엉뚱한 자리를 가른다.
                setDraft((prev) => (prev ? `${prev.replace(/\s+$/, '')}\n\n` : '') + parts.join('\n\n'));
              } catch (err) {
                setUploadError(err instanceof Error ? err.message : '올리지 못했다');
              } finally {
                setUploading(false);
              }
            })();
          }}
          aria-label="새 댓글"
          rows={3}
          /* 조합 상자다 — 목록이 뜨는 동안 커서는 이 칸에 남고, 무엇이
             골라져 있는지는 `aria-activedescendant` 가 말한다. */
          role="combobox"
          aria-expanded={picking}
          aria-controls="comment-mentions"
          aria-autocomplete="list"
          aria-activedescendant={
            picking ? optionId(matches[activeIndex].id) : undefined
          }
          placeholder="댓글을 적는다 — @로 사람 부르기 · ⌘/Ctrl+Enter 로 등록 · 이미지는 붙여넣기"
          className="w-full resize-y rounded-md border border-line bg-panel px-s3 py-s2 text-body leading-body break-keep text-ink max-[720px]:text-lead"
        />
        {picking && (
          /* 팝오버 층 8 — `styles.css` 머리의 z-index 표와 같은 값이다. */
          <ul
            id="comment-mentions"
            role="listbox"
            aria-label="부를 사람"
            className="absolute left-0 top-full z-[8] m-0 mt-s1 max-h-[calc(var(--s8)*6)] w-[calc(var(--s8)*7)] max-w-full list-none overflow-auto rounded-md border border-line bg-panel p-s1 shadow-panel"
          >
            {matches.map((member, index) => (
              <li
                key={member.id}
                id={optionId(member.id)}
                role="option"
                aria-selected={index === activeIndex}
                /* 누르는 동안 포커스를 안 뺏는다 — 커서가 입력칸을 떠나면
                   넣을 자리를 잃는다. */
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(member)}
                className={`flex cursor-pointer items-center gap-s2 rounded-sm px-s2 py-s2 text-meta text-ink max-[720px]:min-h-[44px] ${
                  index === activeIndex ? 'bg-sunk' : ''
                }`}
              >
                <AssigneeMark user={member} size={18} />
                <span className="truncate">{member.display_name}</span>
                {/* 이름만으로는 누구를 부르는지 모른다 — 작업자를 고르는
                    세 자리와 같은 규칙이다. */}
                <span className="ml-auto shrink-0 text-micro text-ink-3">
                  {ROLE_LABEL[member.role]}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-s2 flex items-center justify-end gap-s3">
          {uploadError ? (
            /* 올리기 실패는 이 자리에 남긴다 — 붙여넣은 사람은 사진이 갔는지
               안 갔는지를 그 자리에서 알아야 한다. */
            <p className="m-0 mr-auto text-meta text-st-red-f" role="alert">
              {uploadError}
            </p>
          ) : uploading ? (
            <p className="m-0 mr-auto text-meta text-ink-3">사진을 올리는 중…</p>
          ) : null}
          <Button
            size="sm"
            disabled={sending || uploading || !draft.trim()}
            onClick={() => void submit()}
          >
            {sending ? '다는 중…' : '댓글 달기'}
          </Button>
        </div>
      </div>
    </section>
  );
}
